import { computed, defineComponent, onMounted, onUnmounted, onUpdated, reactive, ref } from 'vue';
import { Popover } from 'bootstrap';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import { RegionAction, RegionGetter } from '@/store/region/region.module';
import { Region } from '@/store/region/region.model';
import { AppointmentRegionAction, AppointmentRegionGetter } from '@/store/appointmentRegion/appointmentRegion.module';
import { AppointmentRegion } from '@/store/appointmentRegion/appointmentRegion.model';
import AppointmentRegionFormComponent from '@/components/appointmentDetail/appointmentRegionForm/AppointmentRegionForm.vue';
import { PerfectScrollbar } from 'vue3-perfect-scrollbar';
import LoadingComponent from '@/components/loading/Loading.vue';
import PaginateComponent from '@/components/paginate/Paginate.vue';

export default defineComponent({
  name: 'AppointmentRegionListComponent',
  components: {
    AppointmentRegionFormComponent,
    PerfectScrollbar,
    LoadingComponent,
    PaginateComponent,
  },
  props: {
    appointmentId: {
      type: Number,
    },
    appointmentStatus: {
      type: Number,
    },
  },
  setup: (props) => {
    const store = useStore();
    const toast = useToast();
    const appointmentRegions = computed<AppointmentRegion[]>(() => store.getters[AppointmentRegionGetter.ALL]);
    const regions = computed<Region[]>(() => {
      const all = store.getters[RegionGetter.ALL] as Region[];
      return all.filter(m => appointmentRegions.value?.every(x => x.region.id !== m.id));
    });
    const searchRegion = computed(() => regions.value.filter((region) => toSearchRegion(region)));
    const searchAppointmentRegions = computed(() => appointmentRegions.value.filter((appointmentRegion) => toSearchAppointmentRegion(appointmentRegion)));
    const displayAppointmentRegions = computed(() => searchAppointmentRegions.value.filter((_, index) => toDisplay(index)));
    const isLoading = computed(() => store.getters[AppointmentRegionGetter.STATUS] === 'init' || store.getters[AppointmentRegionGetter.STATUS] === 'loading');
    const isDrag = ref(false);
    const dragElement = ref(0);
    const popoverList = ref<Popover[]>([]);

    const appointmentRegionForm = ref<InstanceType<typeof AppointmentRegionFormComponent>>();
    const selectedAppointmentRegion = reactive<{
      appointmentId: number,
      regionId?: number | null,
      provinces: string[],
    }>({
      appointmentId: props.appointmentId as number,
      regionId: null,
      provinces: []
    });

    const filterRegion = reactive({
      keyword: '',
      amount: computed(() => searchRegion.value.length),
      total: computed(() => regions.value.length)
    });

    const filterAppointmentRegion = reactive({
      keyword: '',
      amount: computed(() => searchAppointmentRegions.value.length),
      total: computed(() => appointmentRegions.value.length)
    });

    const page = reactive({
      size: 6,
      current: 1
    });

    onMounted(() => {
      store.dispatch(AppointmentRegionAction.FETCH, props.appointmentId);
      store.dispatch(RegionAction.FETCH)
        .then(() => initialPopover());
    });

    // popover
    onUpdated(() => initialPopover());
    onUnmounted(() => {
      popoverList.value.map(element => element.dispose());
    });

    const initialPopover = () => {
      const popoverTriggerList = computed<HTMLElement[]>(() => [].slice.call(document.querySelectorAll('[data-bs-toggle="popover-region"]')) as HTMLElement[]);
      popoverList.value.map(element => element.hide());
      popoverList.value = popoverTriggerList.value?.map(element => new Popover(element, { trigger: 'hover', placement: 'left', html: true }));
    };

    const onDragStart = (event: DragEvent, regionId: string) => {
      isDrag.value = true;
      dragElement.value = parseInt(regionId);
      event.dataTransfer?.setData('regionId', regionId);
    };

    const onDragEnd = () => {
      isDrag.value = false;
      dragElement.value = 0;
    };

    const onDrop = (event: DragEvent) => {
      const regionId = parseInt(event.dataTransfer?.getData('regionId') as string);

      if (regionId) {
        if (!appointmentRegions.value.some(m => m.region.id === regionId)) {
          Promise.resolve(selectedAppointmentRegion.regionId = regionId)
            .then(() => appointmentRegionForm.value?.show());
        } else {
          toast.warning('มีข้อมูลเขตตรวจราชการแล้ว');
        }
      }
    };

    const editClick = (regionId: number) => {
      if (props.appointmentStatus === 0) {
        new Promise<void>((resolve) => {
          const appointmentRegion = appointmentRegions.value.find(m => m.id === regionId) as AppointmentRegion;
          selectedAppointmentRegion.regionId = appointmentRegion.region.id;
          selectedAppointmentRegion.provinces = appointmentRegion.provinces.map(m => m.name);
          resolve();
        }).then(() => appointmentRegionForm.value?.show());
      }
    };

    const clearSelectedAppointmentRegion = () => {
      selectedAppointmentRegion.regionId = null;
      selectedAppointmentRegion.provinces =  [];
    };

    const toSearchRegion = (region: Region): Region | boolean => {
      return (filterRegion.keyword)
        ? region.name.toLowerCase().includes(filterRegion.keyword.toLowerCase())
          ? region
          : false
        : true;
    };

    const toSearchAppointmentRegion = (appointmentRegion: AppointmentRegion): AppointmentRegion | boolean => {
      return (filterAppointmentRegion.keyword)
        ? appointmentRegion.region.name.toLowerCase().includes(filterAppointmentRegion.keyword.toLowerCase())
          ? appointmentRegion
          : false
        : true;
    };

    const toDisplay = (index: number) => {
      const start = (page.current - 1) * page.size;
      const end = start + page.size;
      return index >= start && index < end;
    };

    const selectedPage = (pageNumber: number) => {
      page.current = pageNumber;
    };

    return {
      appointmentRegions,
      searchRegion,
      filterRegion,
      page,
      searchAppointmentRegions,
      displayAppointmentRegions,
      filterAppointmentRegion,
      isLoading,
      isDrag,
      dragElement,
      appointmentRegionForm,
      selectedAppointmentRegion,
      onDragStart,
      onDragEnd,
      onDrop,
      editClick,
      clearSelectedAppointmentRegion,
      selectedPage,
    };
  }
});
